/**
 * First-run portal welcome (Reppr Elite, phase 7) — light theme.
 *
 * Three steps, dismissable, shown once per user. Feature list still respects
 * the show_contracts / show_incentives toggles, and the labels match the
 * portal's actual navigation. Emoji dropped: the design system has none.
 */

import { useState } from 'react'
import type { ClientUser } from '../types/clientPortal'
import styles from './PortalWelcome.module.css'

interface PortalWelcomeProps {
  user: ClientUser
  agencyName: string
  onDismiss: () => void
}

export function PortalWelcome({ user, agencyName, onDismiss }: PortalWelcomeProps) {
  const [currentSlide, setCurrentSlide] = useState(0)

  const firstName = user.name?.split(' ')[0] || null

  const features = [
    { label: 'Your statement', desc: 'What you have been paid, and what your deals guarantee' },
    ...(user.show_contracts ? [{ label: 'Contracts', desc: 'Every agreement you have signed' }] : []),
    ...(user.show_incentives ? [{ label: 'Incentives', desc: 'Performance money and where it stands' }] : []),
    { label: 'Deals in motion', desc: 'What your agency is working on for you' },
    { label: 'Needs you', desc: 'The few things that genuinely need your attention' },
    { label: 'Messages', desc: 'A direct line to your agency' },
    { label: 'Documents', desc: 'Records shared with you, downloadable any time' },
    { label: 'Filings', desc: 'Registrations your representation depends on' },
  ]

  const slides = [
    {
      eyebrow: 'Welcome',
      title: firstName ? `Welcome, ${firstName}.` : 'Welcome.',
      content: (
        <>
          <p className={styles.lead}>
            <span className={styles.strong}>{agencyName}</span> has set up your
            portal — a private view of your money, your agreements and the work
            being done on your behalf.
          </p>
          <p className={styles.lead}>
            Only you and your agency can see what is in here.
          </p>
        </>
      ),
    },
    {
      eyebrow: 'What is here',
      title: 'Everything, in plain terms.',
      content: (
        <>
          <p className={styles.lead}>Your portal covers:</p>
          <div className={styles.featureGrid}>
            {features.map(f => (
              <div key={f.label} className={styles.feature}>
                <div className={styles.featureName}>{f.label}</div>
                <div className={styles.featureDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      eyebrow: 'Ready',
      title: 'Ask whenever something is unclear.',
      content: (
        <>
          <p className={styles.lead}>
            {agencyName} keeps this up to date as deals progress and documents
            are added. If a figure looks wrong, say so — corrections are made at
            the source.
          </p>
          <p className={styles.lead}>
            Go to <span className={styles.strong}>Messages</span> to reach your
            agency directly.
          </p>
        </>
      ),
    },
  ]

  const slide = slides[currentSlide]
  const isLast = currentSlide === slides.length - 1

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Welcome to your portal">
        <div className={styles.steps}>
          {slides.map((s, index) => (
            <button
              key={s.eyebrow}
              onClick={() => setCurrentSlide(index)}
              className={`${styles.step} ${index === currentSlide ? styles.stepActive : ''}`}
              aria-label={`Step ${index + 1}`}
            />
          ))}
        </div>

        <div className={styles.body}>
          <span className={styles.eyebrow}>{slide.eyebrow}</span>
          <h2 className={styles.title}>{slide.title}</h2>
          {slide.content}
        </div>

        <div className={styles.foot}>
          <button onClick={onDismiss} className={styles.skip}>Skip</button>
          <div className={styles.footActions}>
            {currentSlide > 0 && (
              <button onClick={() => setCurrentSlide(currentSlide - 1)} className={styles.back}>
                Back
              </button>
            )}
            <button
              onClick={() => (isLast ? onDismiss() : setCurrentSlide(currentSlide + 1))}
              className={styles.next}
            >
              {isLast ? 'Start' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
